import { useState } from "react";
import { X, FolderOpen, LayoutDashboard, TrendingUp, Trophy, Shield, Building2, UserCircle, MessageCircle, Bookmark, ChevronDown, Languages, Calendar, Lock, HelpCircle } from "lucide-react";
import { t } from "@/lib/i18n";
import { BSSLogo } from "./BSSLogo";

const getOrganSubtitle = (title) => {
  if (!title) return '';
  const match = title.match(/\u2013\s*(.+)/);
  return match ? match[1].trim() : '';
};

export const SidebarMenu = ({ isOpen, onClose, activeSection, onSectionChange, organSections, totalFiles, readProgress, lang, userRole, organAccess, onToggleLang }) => {
  // Default expanded so Organs are visible immediately; remember user preference
  const [organsExpanded, setOrgansExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem('bss.sidebar.learningExpanded');
      return stored === null ? true : stored === 'true';
    } catch (_e) {
      return true;
    }
  });
  const isOrganActive = organSections.some(o => o.id === activeSection);
  const isOrganUnlocked = (idx) => organAccess?.all_unlocked || (organAccess?.unlocked || []).includes(idx + 1);

  const handleToggleLearning = () => {
    const next = !organsExpanded;
    setOrgansExpanded(next);
    try { localStorage.setItem('bss.sidebar.learningExpanded', String(next)); } catch (_e) { /* noop */ }
  };

  // Auto expand when an organ is active
  const expanded = organsExpanded || isOrganActive;

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-overlay" />

      <nav className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar-menu">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <BSSLogo size={52} testId="sidebar-logo" />
          </div>
          <button className="sidebar-close" onClick={onClose} data-testid="sidebar-close-button">
            <X size={20} />
          </button>
        </div>

        <div className="sidebar-nav">
          <button className={`sidebar-item ${activeSection === 'dashboard' ? 'active' : ''}`} onClick={() => onSectionChange('dashboard')} data-testid="sidebar-item-dashboard">
            <LayoutDashboard size={18} strokeWidth={1.8} />
            <span>{t('dashboard', lang)}</span>
          </button>

          <div className="sidebar-section-label">{lang === 'en' ? 'MY ACCOUNT' : 'AKAUN SAYA'}</div>
          <button className={`sidebar-item ${activeSection === 'profile' ? 'active' : ''}`} onClick={() => onSectionChange('profile')} data-testid="sidebar-item-profile">
            <UserCircle size={18} strokeWidth={1.8} />
            <span>{lang === 'en' ? 'My Profile' : 'Profil Saya'}</span>
          </button>
          <button className={`sidebar-item ${activeSection === 'bookmarks' ? 'active' : ''}`} onClick={() => onSectionChange('bookmarks')} data-testid="sidebar-item-bookmarks">
            <Bookmark size={18} strokeWidth={1.8} />
            <span>{lang === 'en' ? 'My Bookmarks' : 'Simpanan Saya'}</span>
          </button>

          {/* Pembelajaran - Collapsible */}
          <button
            className={`sidebar-section-toggle ${isOrganActive ? 'has-active' : ''}`}
            onClick={handleToggleLearning}
            data-testid="sidebar-toggle-learning"
            aria-expanded={expanded}
          >
            <span>{lang === 'en' ? 'LEARNING' : 'PEMBELAJARAN'}</span>
            <ChevronDown size={12} className={`sidebar-chevron ${expanded ? 'open' : ''}`} />
          </button>

          {expanded && (
            <>
              {organSections.map((organ, index) => {
                const organNum = index + 1;
                const subtitle = getOrganSubtitle(organ.title);
                const progress = readProgress ? readProgress[organ.id] : null;
                const progressPct = progress ? progress.percent : 0;
                const unlocked = isOrganUnlocked(index);

                return (
                  <button
                    key={organ.id}
                    className={`sidebar-item sidebar-item-organ ${activeSection === organ.id ? 'active' : ''} ${!unlocked ? 'sidebar-item-locked' : ''}`}
                    onClick={() => onSectionChange(organ.id)}
                    data-testid={`sidebar-item-organ-${organNum}`}
                    title={!unlocked ? (lang === 'en' ? `Locked - attend Organ ${organNum} session to unlock` : `Berkunci - hadir sesi Organ ${organNum} untuk buka`) : ''}
                  >
                    {unlocked ? <FolderOpen size={18} strokeWidth={1.8} /> : <Lock size={16} strokeWidth={1.8} style={{ opacity: 0.5 }} />}
                    <div className="sidebar-item-content">
                      <span className="sidebar-item-title" style={!unlocked ? { opacity: 0.55 } : {}}>Organ {organNum}</span>
                      {subtitle && <span className="sidebar-item-subtitle" style={!unlocked ? { opacity: 0.45 } : {}}>{subtitle}</span>}
                      {progressPct > 0 && unlocked && (
                        <div className="sidebar-progress-bar">
                          <div className="sidebar-progress-fill" style={{ width: `${progressPct}%` }} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}

              <button className={`sidebar-item ${activeSection === 'events' ? 'active' : ''}`} onClick={() => onSectionChange('events')} data-testid="sidebar-item-events">
                <Calendar size={18} strokeWidth={1.8} />
                <span>{lang === 'en' ? 'Events' : 'Sesi & Event'}</span>
              </button>
              <button className={`sidebar-item ${activeSection === 'progress' ? 'active' : ''}`} onClick={() => onSectionChange('progress')} data-testid="sidebar-item-progress">
                <TrendingUp size={18} strokeWidth={1.8} />
                <span>{t('learningProgress', lang)}</span>
              </button>
            </>
          )}

          {/* AKTIVITI */}
          <div className="sidebar-section-label">{lang === 'en' ? 'ACTIVITY' : 'AKTIVITI'}</div>
          <button className={`sidebar-item ${activeSection === 'leaderboard' ? 'active' : ''}`} onClick={() => onSectionChange('leaderboard')} data-testid="sidebar-item-leaderboard">
            <Trophy size={18} strokeWidth={1.8} />
            <span>{t('leaderboard', lang)}</span>
          </button>
          <button className={`sidebar-item ${activeSection === 'community' ? 'active' : ''}`} onClick={() => onSectionChange('community')} data-testid="sidebar-item-community">
            <MessageCircle size={18} strokeWidth={1.8} />
            <span>{lang === 'en' ? 'Community' : 'Komuniti'}</span>
          </button>
          <button className={`sidebar-item ${activeSection === 'faq' ? 'active' : ''}`} onClick={() => onSectionChange('faq')} data-testid="sidebar-item-faq">
            <HelpCircle size={18} strokeWidth={1.8} />
            <span>FAQ</span>
          </button>

          {userRole === 'founder' && (
            <>
              <div className="sidebar-divider" />
              <button className={`sidebar-item ${activeSection === 'founder' ? 'active' : ''}`} onClick={() => onSectionChange('founder')} data-testid="sidebar-item-founder">
                <Building2 size={18} strokeWidth={1.8} />
                <span>{lang === 'en' ? 'Founder Dashboard' : 'Dashboard Founder'}</span>
              </button>
            </>
          )}

          {userRole === 'admin' && (
            <>
              <div className="sidebar-divider" />
              <button className={`sidebar-item ${activeSection === 'admin' ? 'active' : ''}`} onClick={() => onSectionChange('admin')} data-testid="sidebar-item-admin">
                <Shield size={18} strokeWidth={1.8} />
                <span>{lang === 'en' ? 'Admin Panel' : 'Panel Admin'}</span>
              </button>
            </>
          )}

          {/* Mobile-only: Language toggle */}
          <div className="sm:hidden">
            <div className="sidebar-divider" />
            <div className="flex items-center gap-2 px-4 py-2">
              <button
                onClick={onToggleLang}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-[rgba(0,0,0,0.55)] bg-[#F7F7F7] border border-[rgba(0,0,0,0.06)] hover:bg-[#EFEFEF] transition-colors"
                data-testid="sidebar-lang-toggle"
              >
                <Languages size={14} />
                {lang === 'ms' ? 'English' : 'BM'}
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
